import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  ChevronLeft, ChevronRight, Plus, Search, X, Menu, CalendarDays, Keyboard, RefreshCw, Check,
} from 'lucide-react';
import api from '../../../utils/api';
import { Button, Segmented, Modal, Checkbox, errMsg } from '../kit';
import { useLogTime } from '../workflow/LogTimeProvider';
import { useScMe } from '../workflow/participants';
import { useAuth } from '../../../context/AuthContext';
import {
  addDays, startOfDay, ymd, parseYmd, rangeFor, shiftAnchor, titleFor, shortTitleFor, expandAll, isAllDayLike, monthGrid, addMinutes,
} from './calendarMath';
import { COLORS, COLOR_KEYS, autoColor, colorOf, KEY_TYPES, KEY_TYPE_KEYS } from './palette';
import TimeGrid from './TimeGrid';
import MonthGrid from './MonthGrid';
import Agenda from './Agenda';
import MiniMonth from './MiniMonth';
import Floating from './Floating';
import EventCard from './EventCard';
import { QuickCreate, AppointmentSheet, toForm, fromForm } from './EventEditor';
import FeedModal from './FeedModal';
import OtherCalendars from './OtherCalendars';

// Support Coordination calendar, built like Google Calendar: Day, Week, Month and Schedule views,
// a sidebar with a small month, one calendar per coordinator (tick to show, pick its colour) and
// the caseload's key dates. Click or drag on the grid to book; drag to move; drag the bottom edge
// to change the end; click for details, Log time, Mark as done. Repeating appointments ask which
// ones a change is for. The view and date live in the address (?cv=week&d=2026-10-16), so a link
// opens the same place. Personal choices (hidden calendars, colours) stay in this browser.
// scope 'sc' is Support Coordination's calendar (/api/sc/calendar: SC appointments and key dates);
// scope 'email' is the company calendar in Email (/api/calendar: every appointment, linked to an
// SC, PM or client file, with invites).
const VIEWS = [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }, { value: 'schedule', label: 'Schedule' }];
// Phones get three days instead of a cramped week (Google does the same).
const PHONE_VIEWS = [{ value: 'day', label: 'Day' }, { value: '3day', label: '3 days' }, { value: 'month', label: 'Month' }, { value: 'schedule', label: 'Schedule' }];
const ALL_VIEWS = [...VIEWS, PHONE_VIEWS[1]];
const prefsKey = (scope) => (scope === 'sc' ? 'sc:calendar-prefs' : 'mail:calendar-prefs');
const readPrefs = (scope) => { try { return JSON.parse(window.localStorage.getItem(prefsKey(scope)) || '{}') || {}; } catch { return {}; } };
const writePrefs = (scope, p) => { try { window.localStorage.setItem(prefsKey(scope), JSON.stringify(p)); } catch { /* storage blocked: choices last this visit */ } };
const NO_COORD = 'none';
const typing = (el) => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.closest?.('[role="listbox"], [role="combobox"]'));

export default function ScCalendarApp({ staff, onOpenFile, scope = 'sc', sideTop = null, canLogTime = true }) {
  const BASE = scope === 'sc' ? '/sc/calendar' : '/calendar';
  const email = scope === 'email';
  const [sp, setSp] = useSearchParams();
  const view = ALL_VIEWS.some((v) => v.value === sp.get('cv')) ? sp.get('cv') : (typeof window !== 'undefined' && window.innerWidth < 640 ? 'schedule' : 'week');
  const anchor = useMemo(() => parseYmd(sp.get('d')) || startOfDay(new Date()), [sp]);
  const go = useCallback((patch) => setSp((p) => {
    const n = new URLSearchParams(p);
    n.set('view', 'calendar');
    if (patch.view) n.set('cv', patch.view);
    if (patch.date) n.set('d', ymd(patch.date));
    return n;
  }, { replace: true }), [setSp]);

  const scMe = useScMe({ skip: email });
  const { user } = useAuth() || {};
  const me = useMemo(() => (email ? { ...scMe, userId: user && !user.impersonating ? user.id : null } : scMe), [email, scMe, user]);
  const logTime = useLogTime();
  const [prefs, setPrefsState] = useState(() => readPrefs(scope));
  const setPrefs = (patch) => setPrefsState((p) => { const n = { ...p, ...patch }; writePrefs(scope, n); return n; });
  const hidden = useMemo(() => new Set(prefs.hidden || []), [prefs.hidden]);
  const keyOff = useMemo(() => new Set(prefs.keyOff || []), [prefs.keyOff]);
  const calColors = prefs.colors || {};
  const [sidebar, setSidebar] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // ── Data ──────────────────────────────────────────────────────────────────────────────────
  const [r0, r1] = useMemo(() => rangeFor(view, anchor), [view, anchor]);
  const from = ymd(r0); const to = ymd(r1);
  const [data, setData] = useState({ appointments: [], keyDates: [], coordinators: [], outside: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true); setError(null);
    return api.get(`${BASE}/events`, { params: { from, to } })
      .then((r) => { if (id === reqId.current) setData({ appointments: r.data.appointments || [], keyDates: r.data.keyDates || [], coordinators: r.data.coordinators || [], outside: r.data.outside || [] }); })
      .catch((e) => { if (id === reqId.current) setError(errMsg(e, 'We couldn’t load the calendar. Check your connection, then try again.')); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [from, to, BASE]);
  useEffect(() => { load(); }, [load]);
  // The red "now" line moves once a minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // ── Calendars (one per coordinator) ───────────────────────────────────────────────────────
  const calendars = useMemo(() => {
    const list = (data.coordinators || []).map((u) => ({ id: String(u.id), name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Coordinator' }));
    list.sort((a, b) => (a.id === String(me.userId) ? -1 : b.id === String(me.userId) ? 1 : a.name.localeCompare(b.name)));
    list.push({ id: NO_COORD, name: email ? 'No one' : 'No coordinator' });
    return list;
  }, [data.coordinators, me.userId]);
  const calColor = useCallback((id) => calColors[id || NO_COORD] || (id ? autoColor(id) : 'graphite'), [calColors]);
  const colorFor = useCallback((o) => {
    if (o.kind === 'keydate') return colorOf(KEY_TYPES[o.type]?.color);
    if (o.kind === 'outside') return colorOf(calColors.outlook || 'graphite');
    return colorOf(o.color || calColor(o.coordinator_id));
  }, [calColor]);

  // ── Occurrences on screen ─────────────────────────────────────────────────────────────────
  const rangeEnd = useMemo(() => addMinutes(addDays(r1, 1), -1), [r1]);
  const allOccs = useMemo(() => {
    const appts = expandAll(data.appointments, r0, rangeEnd, { showCancelled: !!prefs.showCancelled }).map((o) => ({ ...o, kind: o.kind || 'meeting' }));
    const keys = (data.keyDates || []).filter((k) => KEY_TYPES[k.type] && !keyOff.has(k.type)).map((k) => {
      const d = parseYmd(String(k.date).slice(0, 10));
      if (!d) return null;
      const t = KEY_TYPES[k.type];
      return {
        key: `kd:${k.type}:${k.ref_id || k.coordination_id}:${ymd(d)}`, kind: 'keydate', type: k.type, typeLabel: t.one, icon: t.icon,
        title: k.title, participant: k.participant, coordination_id: k.coordination_id, coordinator_id: k.coordinator_id, ref_id: k.ref_id,
        start: d, end: addDays(d, 1), all_day: true, readOnly: true,
      };
    }).filter(Boolean);
    const outside = (data.outside || []).map((x) => ({
      key: `out:${x.remote_id}`, kind: 'outside', title: x.title, email: x.email, calendarId: 'outlook',
      start: new Date(x.start_at), end: new Date(x.end_at), all_day: !!x.all_day, readOnly: true,
    })).filter((o) => o.start < rangeEnd && o.end > r0);
    return [...appts, ...keys, ...outside];
  }, [data, r0, rangeEnd, prefs.showCancelled, keyOff]);
  const q = search.trim().toLowerCase();
  const occs = useMemo(() => allOccs.filter((o) => {
    if (o.kind === 'outside') { if (hidden.has('outlook')) return false; }
    else if (hidden.has(o.coordinator_id ? String(o.coordinator_id) : NO_COORD)) return false;
    if (!q) return true;
    return [o.title, o.participant, o.location, o.coordinator_name, o.typeLabel].filter(Boolean).join(' ').toLowerCase().includes(q);
  }), [allOccs, hidden, q]);
  const busyDays = useMemo(() => new Set(occs.map((o) => ymd(o.start))), [occs]);

  // ── Floating card, editor, scope question ─────────────────────────────────────────────────
  const [float, setFloat] = useState(null);   // { type: 'details'|'quick'|'more', occ?, draft?, rect, day? }
  const [sheet, setSheet] = useState(null);   // { editing: occ|null, form }
  const [scopeAsk, setScopeAsk] = useState(null); // { verb, occ, resolve }
  const [saving, setSaving] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  const closeFloat = useCallback(() => setFloat(null), []);
  const askScope = (verb, occ) => new Promise((resolve) => setScopeAsk({ verb, occ, resolve }));

  const seriesOf = (occ) => data.appointments.find((a) => String(a.id) === String(occ.id));
  const openCreate = useCallback((range, rect) => {
    let { start, end, allDay } = range || {};
    if (!start) {
      // From the Create button: the next half hour on the day in view.
      const t = new Date();
      start = new Date(anchor); start.setHours(t.getHours(), t.getMinutes() < 30 ? 30 : 60, 0, 0);
      end = addMinutes(start, 60); allDay = false;
    } else if (range.fromMonth) { start = new Date(start); start.setHours(9, 0, 0, 0); end = addMinutes(start, 60); allDay = false; }
    // A new appointment is yours unless you change it.
    const draft = { start, end, allDay, form: toForm({ start, end, allDay, coordinator_id: me.userId ? String(me.userId) : '' }) };
    if (!rect || window.innerWidth < 640) setSheet({ editing: null, form: draft.form });
    else setFloat({ type: 'quick', draft, rect });
  }, [anchor, me.userId]);

  // ── Saving ────────────────────────────────────────────────────────────────────────────────
  const afterSave = async (msg) => { await load(); if (msg) toast.success(msg); };
  // What the server says about the invites, added to the message (or shown as a problem).
  const withInvites = (data, msg) => {
    const inv = data && data.invites;
    if (inv && inv.error) { toast.error(inv.error); return msg; }
    return inv && inv.sent ? `${msg}. Invites sent to ${inv.sent} ${inv.sent === 1 ? 'person' : 'people'}` : msg;
  };
  const create = async (form) => {
    const r = fromForm(form, scope);
    if (r.error) { toast.error(r.error); return false; }
    setSaving(true);
    try { const res = await api.post(`${BASE}/appointments`, r.body); await afterSave(withInvites(res.data, 'Appointment booked')); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
    finally { setSaving(false); }
  };
  // body is for the occurrence that was opened. 'all' on a series moves the series by the same
  // amount the occurrence moved.
  const patch = async (occ, body, scope) => {
    const send = { ...body };
    if (occ.recurring) {
      send.scope = scope || 'all';
      send.occurrence_date = occ.occurrence_date;
      if (send.scope === 'all' && (body.start_at || body.end_at)) {
        const s = seriesOf(occ);
        const ns = new Date(body.start_at || occ.start); const ne = new Date(body.end_at || occ.end);
        const shift = ns - occ.start;
        const seriesStart = new Date(new Date(s.start_at).getTime() + shift);
        send.start_at = seriesStart.toISOString();
        send.end_at = new Date(seriesStart.getTime() + (ne - ns)).toISOString();
      }
    }
    return (await api.patch(`${BASE}/appointments/${occ.id}`, send)).data;
  };
  const saveSheet = async () => {
    const { editing, form } = sheet;
    if (!editing) { if (await create(form)) setSheet(null); return; }
    const r = fromForm(form, scope);
    if (r.error) { toast.error(r.error); return; }
    let which;
    if (editing.recurring) { which = await askScope('edit', editing); if (!which) return; }
    setSaving(true);
    try {
      const body = { ...r.body };
      // Changing one day of a series never changes how it repeats.
      if (which === 'this') { delete body.repeat; delete body.repeat_until; }
      const out = await patch(editing, body, which);
      setSheet(null);
      await afterSave(withInvites(out, 'Appointment saved'));
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const saveQuick = async () => {
    if (await create(float.draft.form)) setFloat(null);
  };

  // Drag to move or resize. A one-off moves at once (with Undo); a repeating one asks first.
  const onChange = async (occ, start, end) => {
    if (occ.readOnly || occ.kind === 'keydate') return;
    let scope;
    if (occ.recurring) { scope = await askScope('move', occ); if (!scope) return; }
    const before = data;
    if (!occ.recurring) {
      setData((d) => ({ ...d, appointments: d.appointments.map((a) => (String(a.id) === String(occ.id) ? { ...a, start_at: start.toISOString(), end_at: end.toISOString() } : a)) }));
    }
    try {
      await patch(occ, { start_at: start.toISOString(), end_at: end.toISOString() }, scope);
      await load();
      if (!occ.recurring) {
        toast((t) => (
          <span className="flex items-center gap-3 text-[13px]">Appointment moved
            <button type="button" className="font-semibold text-indigo-600 hover:underline" onClick={async () => {
              toast.dismiss(t.id);
              try { await api.patch(`${BASE}/appointments/${occ.id}`, { start_at: occ.start.toISOString(), end_at: occ.end.toISOString() }); await load(); } catch (e) { toast.error(errMsg(e)); }
            }}>Undo</button>
          </span>
        ), { duration: 6000 });
      } else toast.success('Appointment moved');
    } catch (e) { setData(before); toast.error(errMsg(e)); }
  };

  const remove = async (occ) => {
    let which = 'all';
    if (occ.recurring) { which = await askScope('delete', occ); if (!which) return; }
    if ((occ.attendees || []).length && occ.invites_sent_at && !window.confirm(`The ${occ.attendees.length === 1 ? 'person' : `${occ.attendees.length} people`} invited will be told it is ${which === 'all' ? 'cancelled' : 'changed'}. Go ahead?`)) return;
    setFloat(null); setSheet(null);
    try {
      const s = occ.recurring ? null : seriesOf(occ);
      const res = await api.delete(`${BASE}/appointments/${occ.id}`, { params: occ.recurring ? { scope: which, date: occ.occurrence_date } : {} });
      if (res.data && res.data.invites && res.data.invites.error) toast.error(res.data.invites.error);
      await load();
      if (s && !(s.attendees || []).length) {
        toast((t) => (
          <span className="flex items-center gap-3 text-[13px]">Appointment deleted
            <button type="button" className="font-semibold text-indigo-600 hover:underline" onClick={async () => {
              toast.dismiss(t.id);
              const { id, participant, coordinator_name, client_id, created_at, updated_at, ical_uid, ical_sequence, invites_sent_at, ...rest } = s;
              try { await api.post(`${BASE}/appointments`, { ...rest, ...(s.module === 'pm' || s.module === 'care' ? { client_id } : {}), send_invites: false }); await load(); } catch (e) { toast.error(errMsg(e)); }
            }}>Undo</button>
          </span>
        ), { duration: 6000 });
      } else toast.success(which === 'this' ? 'That day was removed from the series' : (occ.attendees || []).length && occ.invites_sent_at ? 'Deleted. The people invited were told.' : 'Appointment deleted');
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (occ, status) => {
    setFloat(null);
    try { await patch(occ, { status }, 'this'); await afterSave(status === 'completed' ? 'Marked as done' : status === 'cancelled' ? 'Marked as cancelled' : 'Back to booked'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openLogTime = (occ) => {
    setFloat(null);
    const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    logTime({
      coordinationId: occ.coordination_id, participantName: occ.participant,
      defaults: { note_date: ymd(occ.start), start_time: hhmm(occ.start), end_time: hhmm(occ.end), summary: occ.title },
      onSaved: async (entry) => {
        try { await patch(occ, { status: 'completed', ...(entry && entry.id ? { time_entry_id: entry.id } : {}) }, 'this'); await load(); }
        catch { /* the time is saved; the appointment just isn't marked */ }
      },
    });
  };
  const taskDone = async (occ) => {
    setFloat(null);
    try { await api.patch(`/support-coordination/tasks/${occ.ref_id}`, { status: 'done' }); await afterSave('Task done'); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const editOcc = (occ) => { setFloat(null); setSheet({ editing: occ, form: toForm(occ) }); };
  const resend = async (occ) => {
    setFloat(null);
    try { const r = await api.post(`${BASE}/appointments/${occ.id}/invites`); await afterSave(withInvites(r.data, 'Invite sent')); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const duplicate = (occ) => {
    setFloat(null);
    const f = toForm(occ);
    setSheet({ editing: null, form: { ...f, status: 'scheduled', repeat: 'none', repeat_until: '' } });
  };

  // ── Navigation and keys ───────────────────────────────────────────────────────────────────
  const move = (dir) => go({ date: shiftAnchor(view, anchor, dir) });
  const today = () => go({ date: startOfDay(new Date()) });
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target) || float || sheet || scopeAsk || feedOpen || keysOpen) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      const k = e.key.toLowerCase();
      const act = {
        t: today, d: () => go({ view: 'day' }), w: () => go({ view: 'week' }), m: () => go({ view: 'month' }), a: () => go({ view: 'schedule' }),
        j: () => move(1), n: () => move(1), k: () => move(-1), p: () => move(-1), c: () => openCreate(null, null),
        '/': () => { setSearchOpen(true); setTimeout(() => searchRef.current && searchRef.current.focus(), 10); }, '?': () => setKeysOpen(true),
      }[e.key === '?' ? '?' : k];
      if (act) { e.preventDefault(); act(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const toggleCal = (id) => {
    const n = new Set(hidden);
    if (n.has(id)) n.delete(id); else n.add(id);
    setPrefs({ hidden: [...n] });
  };
  const onlyMine = () => setPrefs({ hidden: calendars.filter((c) => c.id !== String(me.userId)).map((c) => c.id) });
  const showAll = () => setPrefs({ hidden: [] });
  const toggleKey = (t) => {
    const n = new Set(keyOff);
    if (n.has(t)) n.delete(t); else n.add(t);
    setPrefs({ keyOff: [...n] });
  };

  // ── Render ────────────────────────────────────────────────────────────────────────────────
  const days = view === 'day' ? [anchor] : view === 'week' ? Array.from({ length: 7 }, (_, i) => addDays(r0, i)) : view === '3day' ? [0, 1, 2].map((i) => addDays(r0, i)) : [];
  const timed = occs.filter((o) => !isAllDayLike(o));
  const allDay = occs.filter(isAllDayLike);
  const selectedKey = float && float.occ ? float.occ.key : null;
  const draftShown = float && float.type === 'quick' ? { ...float.draft, title: float.draft.form.title } : null;

  const sidebarBody = (
    <div className="p-4 space-y-6">
      {sideTop}
      <button type="button" onClick={() => openCreate(null, null)}
        className="inline-flex items-center gap-3 h-14 pl-4 pr-6 rounded-2xl bg-white text-slate-800 font-semibold text-[14px] shadow-[0_1px_3px_rgba(15,23,42,0.12),0_4px_12px_-2px_rgba(15,23,42,0.12)] hover:shadow-[0_2px_6px_rgba(15,23,42,0.14),0_10px_24px_-6px_rgba(15,23,42,0.2)] hover:bg-indigo-50/40 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center"><Plus size={18} aria-hidden /></span>
        Create
      </button>
      <MiniMonth anchor={anchor} range={view === 'week' || view === 'day' ? [r0, r1] : null} busyDays={busyDays}
        onPick={(d) => { go({ date: d }); if (window.innerWidth < 1024) setSidebar(false); }} />

      <section aria-labelledby="sc-cal-mine">
        <div className="flex items-center justify-between mb-1.5">
          <h3 id="sc-cal-mine" className="text-[12.5px] font-semibold text-slate-800">{email ? 'People' : 'Coordinators'}</h3>
          <div className="flex gap-2 text-[11.5px] font-semibold">
            {me.userId && <button type="button" onClick={onlyMine} className="text-indigo-600 hover:underline">Only mine</button>}
            {hidden.size > 0 && <button type="button" onClick={showAll} className="text-slate-500 hover:text-slate-800 hover:underline">Show all</button>}
          </div>
        </div>
        <ul className="space-y-0.5">
          {calendars.map((c) => (
            <CalendarRow key={c.id} label={c.id === String(me.userId) ? `${c.name} (you)` : c.name} color={calColor(c.id === NO_COORD ? null : c.id)}
              checked={!hidden.has(c.id)} onToggle={() => toggleCal(c.id)}
              onColor={(k) => setPrefs({ colors: { ...calColors, [c.id]: k } })} />
          ))}
          {email && (data.outside || []).length > 0 && (
            <CalendarRow label="My Outlook events" color={calColors.outlook || 'graphite'} checked={!hidden.has('outlook')} onToggle={() => toggleCal('outlook')}
              onColor={(k) => setPrefs({ colors: { ...calColors, outlook: k } })} />
          )}
        </ul>
      </section>

      {email && <OtherCalendars onSynced={load} />}
      {!email && <section aria-labelledby="sc-cal-keys">
        <h3 id="sc-cal-keys" className="text-[12.5px] font-semibold text-slate-800 mb-1.5">Key dates</h3>
        <ul className="space-y-0.5">
          {KEY_TYPE_KEYS.map((t) => (
            <CalendarRow key={t} label={KEY_TYPES[t].label} color={KEY_TYPES[t].color} checked={!keyOff.has(t)} onToggle={() => toggleKey(t)} />
          ))}
        </ul>
        <p className="text-[11.5px] text-slate-500 mt-2 leading-snug">Plan reviews, reports, funding releases and tasks come from each participant’s file.</p>
      </section>}

      <div className="space-y-2 pt-1">
        <Checkbox checked={!!prefs.showCancelled} onChange={(v) => setPrefs({ showCancelled: v })} label="Show cancelled appointments" />
        <div className="flex flex-wrap gap-2 pt-1">
          {!email && <Button size="sm" variant="secondary" icon={CalendarDays} onClick={() => setFeedOpen(true)}>Subscribe</Button>}
          <Button size="sm" variant="ghost" icon={Keyboard} onClick={() => setKeysOpen(true)}>Shortcuts</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative flex h-[calc(100dvh-7rem)] min-h-[560px] bg-white rounded-2xl border border-slate-200/80 shadow-card overflow-hidden sc-calendar">
      {/* Sidebar: in the page on wide screens, over it on narrow ones */}
      {sidebar && (
        <>
          <div className="lg:hidden absolute inset-0 z-20 bg-slate-900/20" onClick={() => setSidebar(false)} aria-hidden />
          <aside className="absolute lg:static z-30 inset-y-0 left-0 w-72 lg:w-64 flex-shrink-0 bg-white border-r border-slate-200/80 overflow-y-auto sc-cal-scroll" aria-label="Calendar options">
            {sidebarBody}
          </aside>
        </>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-3 h-14 border-b border-slate-200/80 flex-shrink-0">
          <button type="button" onClick={() => setSidebar((v) => !v)} aria-label={sidebar ? 'Hide the sidebar' : 'Show the sidebar'} aria-expanded={sidebar}
            className="w-10 h-10 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100"><Menu size={19} aria-hidden /></button>
          <Button variant="secondary" size="sm" onClick={today} className="hidden sm:inline-flex !rounded-full !px-4">Today</Button>
          <button type="button" onClick={today} aria-label="Today" title="Today"
            className="sm:hidden w-9 h-9 flex-shrink-0 rounded-lg ring-1 ring-inset ring-slate-300 text-[13px] font-bold text-slate-700 tabular-nums flex items-center justify-center">{now.getDate()}</button>
          <div className="flex flex-shrink-0">
            <button type="button" onClick={() => move(-1)} aria-label={`Previous ${view === 'schedule' ? '30 days' : view === '3day' ? '3 days' : view}`} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronLeft size={19} aria-hidden /></button>
            <button type="button" onClick={() => move(1)} aria-label={`Next ${view === 'schedule' ? '30 days' : view === '3day' ? '3 days' : view}`} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronRight size={19} aria-hidden /></button>
          </div>
          <h2 className="text-[16px] sm:text-[20px] font-semibold text-slate-900 tracking-[-0.01em] truncate min-w-0" aria-live="polite">
            <span className="sm:hidden">{shortTitleFor(view, anchor)}</span><span className="hidden sm:inline">{titleFor(view, anchor)}</span>
          </h2>
          {loading && <RefreshCw size={14} className="animate-spin text-slate-400 flex-shrink-0" aria-label="Loading" />}
          <div className="ml-auto flex items-center gap-1.5">
            {searchOpen ? (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
                <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search appointments" aria-label="Search appointments"
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearch(''); setSearchOpen(false); } }}
                  className="h-9 w-40 sm:w-56 pl-8 pr-8 rounded-full bg-slate-100 text-[13px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                <button type="button" aria-label="Close search" onClick={() => { setSearch(''); setSearchOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} aria-hidden /></button>
              </div>
            ) : (
              <button type="button" aria-label="Search appointments" onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current && searchRef.current.focus(), 10); }}
                className="w-10 h-10 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100"><Search size={18} aria-hidden /></button>
            )}
            <div className="hidden md:block"><Segmented value={view === '3day' ? 'week' : view} onChange={(v) => go({ view: v })} options={VIEWS} size="sm" /></div>
            <div className="md:hidden">
              <ViewMenu value={view} options={typeof window !== 'undefined' && window.innerWidth < 640 ? PHONE_VIEWS : VIEWS} onChange={(v) => go({ view: v })} />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-rose-50 text-rose-800 text-[13px] border-b border-rose-100" role="alert">
            {error}<Button size="sm" variant="secondary" icon={RefreshCw} onClick={load}>Try again</Button>
          </div>
        )}
        {q && <div className="px-4 py-1.5 text-[12px] text-slate-600 bg-amber-50/70 border-b border-amber-100">Showing {occs.length} {occs.length === 1 ? 'match' : 'matches'} for “{search.trim()}” in this {view === 'schedule' ? 'list' : view}.</div>}

        <div className="flex-1 min-h-0">
          {(view === 'day' || view === 'week' || view === '3day') && (
            <TimeGrid days={days} occs={timed} allDay={allDay} colorFor={colorFor} now={now} selectedKey={selectedKey} draft={draftShown}
              onSelect={(occ, rect) => setFloat({ type: 'details', occ, rect })}
              onCreate={openCreate} onChange={onChange} onDayClick={(d) => go({ view: 'day', date: d })} />
          )}
          {view === 'month' && (
            <MonthGrid grid={monthGrid(anchor)} anchor={anchor} occs={occs} colorFor={colorFor} now={now} selectedKey={selectedKey}
              onSelect={(occ, rect) => setFloat({ type: 'details', occ, rect })}
              onCreate={openCreate} onChange={onChange} onDayClick={(d) => go({ view: 'day', date: d })}
              onMore={(day, rect) => setFloat({ type: 'more', day, rect })} />
          )}
          {view === 'schedule' && (
            <Agenda from={r0} to={r1} occs={occs} colorFor={colorFor} now={now} search={q}
              onSelect={(occ, rect) => setFloat({ type: 'details', occ, rect })} onCreate={() => openCreate(null, null)} />
          )}
        </div>
      </div>

      {/* Phone: the Create button floats */}
      <button type="button" onClick={() => openCreate(null, null)} aria-label="Create an appointment"
        className="sm:hidden absolute right-4 bottom-4 z-10 w-14 h-14 rounded-2xl bg-indigo-600 text-white shadow-lg flex items-center justify-center"><Plus size={24} aria-hidden /></button>

      {float && float.type === 'details' && float.occ.kind === 'outside' && (
        <Floating rect={float.rect} onClose={closeFloat} label={float.occ.title}>
          <div className="p-5">
            <div className="flex gap-3.5">
              <span className="mt-1.5 w-3.5 h-3.5 rounded-[4px] flex-shrink-0" style={{ background: colorFor(float.occ).hex }} aria-hidden />
              <div className="min-w-0">
                <h2 className="text-[19px] font-semibold text-slate-900 break-words">{float.occ.title}</h2>
                <p className="text-[13px] text-slate-600 mt-0.5">{float.occ.start.toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' })} – {float.occ.end.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</p>
                <p className="text-[12.5px] text-slate-500 mt-3">From your Outlook calendar ({float.occ.email}). Only you see it here; change it in Outlook.</p>
              </div>
            </div>
            <div className="flex justify-end mt-3"><Button size="sm" variant="secondary" onClick={closeFloat}>Close</Button></div>
          </div>
        </Floating>
      )}
      {float && float.type === 'details' && float.occ.kind !== 'outside' && (
        <Floating rect={float.rect} onClose={closeFloat} label={float.occ.title}>
          <EventCard occ={float.occ} color={colorFor(float.occ)} onClose={closeFloat}
            canLog={canLogTime && !!float.occ.coordination_id} scope={scope} onResend={() => resend(float.occ)}
            onEdit={() => editOcc(float.occ)} onDelete={() => remove(float.occ)} onStatus={(s) => setStatus(float.occ, s)}
            onLogTime={() => openLogTime(float.occ)} onDuplicate={() => duplicate(float.occ)} onTaskDone={() => taskDone(float.occ)}
            onOpenFile={() => { const o = float.occ; setFloat(null); onOpenFile(o.coordination_id, o); }} />
        </Floating>
      )}
      {float && float.type === 'quick' && (
        <Floating rect={float.rect} onClose={closeFloat} label="New appointment" width={420}>
          <QuickCreate draft={float.draft} me={me} saving={saving} scope={scope}
            onChange={(draft) => setFloat((f) => ({ ...f, draft }))} onSave={saveQuick}
            onMore={() => { const form = float.draft.form; setFloat(null); setSheet({ editing: null, form }); }} />
        </Floating>
      )}
      {float && float.type === 'more' && (
        <Floating rect={float.rect} onClose={closeFloat} label="Appointments that day" width={300}>
          <DayList day={float.day} occs={occs} colorFor={colorFor}
            onPick={(occ, rect) => setFloat({ type: 'details', occ, rect })} onClose={closeFloat} />
        </Floating>
      )}

      <AppointmentSheet open={!!sheet} editing={sheet && sheet.editing} form={sheet && sheet.form}
        onForm={(form) => setSheet((s) => ({ ...s, form }))} onClose={() => setSheet(null)} onSave={saveSheet}
        onDelete={() => sheet && sheet.editing && remove(sheet.editing)} saving={saving}
        me={me} coordinators={data.coordinators} staff={staff} occs={allOccs} scope={scope} />

      <ScopeModal ask={scopeAsk} onDone={(scope) => { if (scopeAsk) scopeAsk.resolve(scope); setScopeAsk(null); }} />
      {!email && <FeedModal open={feedOpen} onClose={() => setFeedOpen(false)} />}
      <ShortcutsModal open={keysOpen} onClose={() => setKeysOpen(false)} />
    </div>
  );
}

// One calendar in the sidebar: tick to show it, the dot opens its colour.
function CalendarRow({ label, color, checked, onToggle, onColor }) {
  const c = COLORS[color] || COLORS.graphite;
  return (
    <li className="group flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-slate-50">
      <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle}
        className="flex items-center gap-2.5 flex-1 min-w-0 text-left text-[13px] text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded">
        <span className="w-[18px] h-[18px] rounded-[5px] flex items-center justify-center flex-shrink-0 ring-2 ring-inset transition-colors"
          style={{ background: checked ? c.hex : 'transparent', boxShadow: `inset 0 0 0 2px ${c.hex}` }}>
          {checked && <Check size={12} strokeWidth={3} style={{ color: c.ink }} aria-hidden />}
        </span>
        <span className="truncate">{label}</span>
      </button>
      {onColor && (
        <RMenu.Root>
          <RMenu.Trigger asChild>
            <button type="button" aria-label={`Colour for ${label}`} className="opacity-0 group-hover:opacity-100 focus:opacity-100 w-6 h-6 rounded-full flex items-center justify-center hover:bg-slate-200/70">
              <span className="w-3 h-3 rounded-full" style={{ background: c.hex }} aria-hidden />
            </button>
          </RMenu.Trigger>
          <RMenu.Portal>
            <RMenu.Content align="end" sideOffset={4} className="z-[90] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-2.5 grid grid-cols-6 gap-1.5">
              {COLOR_KEYS.map((k) => (
                <RMenu.Item key={k} onSelect={() => onColor(k)} aria-label={COLORS[k].label}
                  className="w-6 h-6 rounded-full cursor-pointer outline-none data-[highlighted]:ring-2 data-[highlighted]:ring-offset-1 data-[highlighted]:ring-slate-400 flex items-center justify-center"
                  style={{ background: COLORS[k].hex }}>
                  {k === color && <Check size={12} strokeWidth={3} style={{ color: COLORS[k].ink }} aria-hidden />}
                </RMenu.Item>
              ))}
            </RMenu.Content>
          </RMenu.Portal>
        </RMenu.Root>
      )}
    </li>
  );
}

function ViewMenu({ value, options, onChange }) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <Button variant="secondary" size="sm" className="!rounded-full">{ALL_VIEWS.find((v) => v.value === value)?.label}</Button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="end" sideOffset={4} className="z-[90] min-w-[160px] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1">
          {options.map((v) => (
            <RMenu.Item key={v.value} onSelect={() => onChange(v.value)}
              className={clsx('px-3 py-2.5 rounded-lg text-[13.5px] cursor-pointer outline-none data-[highlighted]:bg-slate-100', v.value === value ? 'font-semibold text-indigo-700' : 'text-slate-700')}>
              {v.label}
            </RMenu.Item>
          ))}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// Everything on one day (from "N more" in the month view).
function DayList({ day, occs, colorFor, onPick, onClose }) {
  const d0 = startOfDay(day); const d1 = addDays(d0, 1);
  const list = occs.filter((o) => o.start < d1 && o.end > d0);
  return (
    <div className="p-3">
      <div className="flex items-center justify-between px-1 pb-2">
        <span className="text-[13px] font-semibold text-slate-800">{titleFor('day', day)}</span>
        <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100"><X size={15} aria-hidden /></button>
      </div>
      <ul className="space-y-0.5">
        {list.map((o) => {
          const c = colorFor(o);
          const allDay = isAllDayLike(o);
          return (
            <li key={o.key}>
              <button type="button" onClick={(e) => onPick(o, e.currentTarget.getBoundingClientRect())}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-100"
                style={allDay ? { background: o.kind === 'keydate' ? undefined : c.hex, color: o.kind === 'keydate' ? c.hex : c.ink } : undefined}>
                {!allDay && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.hex }} aria-hidden />}
                {!allDay && <span className="text-slate-500 tabular-nums">{`${o.start.getHours() % 12 || 12}${o.start.getMinutes() ? `:${String(o.start.getMinutes()).padStart(2, '0')}` : ''}${o.start.getHours() < 12 ? 'am' : 'pm'}`}</span>}
                <span className="font-semibold truncate">{o.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Which appointments of a repeating series a change is for (Google's three choices).
function ScopeModal({ ask, onDone }) {
  const [choice, setChoice] = useState('this');
  useEffect(() => { if (ask) setChoice('this'); }, [ask]);
  const verb = ask ? ask.verb : 'edit';
  const title = verb === 'delete' ? 'Delete repeating appointment' : verb === 'move' ? 'Move repeating appointment' : 'Change repeating appointment';
  const opts = [
    { value: 'this', label: 'This appointment' },
    { value: 'following', label: 'This and following appointments' },
    { value: 'all', label: 'All appointments' },
  ];
  return (
    <Modal open={!!ask} onClose={() => onDone(null)} title={title} width="sm"
      footer={<><Button variant="secondary" onClick={() => onDone(null)}>Cancel</Button><Button variant={verb === 'delete' ? 'danger' : 'primary'} onClick={() => onDone(choice)}>OK</Button></>}>
      <div role="radiogroup" aria-label="Which appointments" className="space-y-1">
        {opts.map((o) => (
          <label key={o.value} className="flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer hover:bg-slate-50 text-[14px] text-slate-800">
            <input type="radio" name="sc-scope" value={o.value} checked={choice === o.value} onChange={() => setChoice(o.value)} className="w-4 h-4 accent-indigo-600" />
            {o.label}
          </label>
        ))}
      </div>
    </Modal>
  );
}

function ShortcutsModal({ open, onClose }) {
  const rows = [
    ['T', 'Go to today'], ['D', 'Day view'], ['W', 'Week view'], ['M', 'Month view'], ['A', 'Schedule view'],
    ['J or N', 'Next'], ['K or P', 'Previous'], ['C', 'Book an appointment'], ['/', 'Search'], ['Esc', 'Close or cancel a drag'],
  ];
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" width="sm" footer={<Button onClick={onClose}>Done</Button>}>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-2 text-[13.5px]">
            <dt className="text-slate-700">{v}</dt>
            <dd><kbd className="px-2 py-0.5 rounded-md bg-slate-100 ring-1 ring-inset ring-slate-200 text-[12px] font-semibold text-slate-700">{k}</kbd></dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}
